// src/features/reports/reportStyles.js
//
// Shared styling helpers for the three portfolio reports. Pulled out
// so the per-report files stay focused on data shape + layout.
//
// Color thresholds match the legacy monolith:
//   Lease: LEASE_DAYS_OVERDUE=0, URGENT=90, SOON_1=180, SOON_2=365
//   Capacity: <70% optimal/green, 70-90% amber, >=90% red
//   Utilization (peakRatio): <0.6 green, 0.6-0.85 amber, >=0.85 red

import { useRef } from "react";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { T } from "../../app/tokens.js";
import { HeadingTooltip } from "../../components/HeadingTooltip.jsx";
import { ColorLegendPopup } from "../../components/ColorLegendPopup.jsx";
import { TableSortCaret } from "../../v2/utils/tableThStyle.jsx";

// Keyboard-accessible sort headers need a :focus-visible ring that inline
// styles cannot express. A scoped <style> block is injected once per table
// by each report that renders SortableTh; the class name is intentionally
// specific to avoid collisions. Mirrors the GANTT_TH_CSS / LEASES_TH_CSS
// pattern in PortfolioGantt.jsx and LeasesListTable.jsx.
export const REPORT_TH_CSS = `
.report-sort-th:focus-visible {
  outline: 2px solid ${T.blue};
  outline-offset: -2px;
}
`;

// Legacy thresholds — keep in sync with monolith L225-228.
export const LEASE_DAYS_OVERDUE = 0;
export const LEASE_DAYS_URGENT  = 90;
export const LEASE_DAYS_SOON_1  = 180;
export const LEASE_DAYS_SOON_2  = 365;

/**
 * Map daysRemaining → {color, bg, label}. Mirrors the monolith band
 * logic at L6334 + L6462.
 *
 * @param {number|null|undefined} days
 */
export function leaseUrgency(days) {
  if (days == null) {
    return { color: T.light, bg: T.lightGray, label: "—" };
  }
  if (days <= LEASE_DAYS_OVERDUE) {
    return { color: T.white, bg: T.red, label: "Overdue" };
  }
  if (days < LEASE_DAYS_URGENT) {
    return { color: T.white, bg: T.red, label: "<90d" };
  }
  if (days < LEASE_DAYS_SOON_1) {
    return { color: T.dark, bg: T.utilAmber, label: "<6mo" };
  }
  if (days < LEASE_DAYS_SOON_2) {
    return { color: T.dark, bg: T.utilAmber, label: "<1yr" };
  }
  return { color: T.dark, bg: T.utilGreen, label: "1yr+" };
}

/**
 * Map a 0-1 capacity ratio (headcount / installedCapacity) to color.
 * Null is rendered as "no data" gray.
 */
export function capacityBand(ratio) {
  if (ratio == null) return { color: T.light, bg: T.lightGray, label: "No data" };
  if (ratio >= 1.0)  return { color: T.white, bg: T.red,    label: "Over capacity" };
  if (ratio >= 0.9)  return { color: T.white, bg: T.red,    label: "Near capacity" };
  if (ratio >= 0.7)  return { color: T.dark,  bg: T.utilAmber, label: "Optimal" };
  if (ratio >= 0.5)  return { color: T.dark,  bg: T.utilAmber, label: "Under-used" };
  return                  { color: T.dark,  bg: T.utilGreen, label: "Over-provisioned" };
}

/**
 * Map a 0-1 peak utilization ratio to color. Different break points
 * than capacity since peak attendance is naturally lower than seat fill.
 */
export function utilizationBand(ratio) {
  if (ratio == null) return { color: T.light, bg: T.lightGray, label: "No data" };
  if (ratio >= 0.85) return { color: T.white, bg: T.red,    label: "Over-used" };
  if (ratio >= 0.6)  return { color: T.dark,  bg: T.utilAmber, label: "Healthy" };
  return                  { color: T.dark,  bg: T.utilGreen, label: "Under-used" };
}

/** "0.92" → "92%", null → "—" */
export function pct(ratio) {
  if (ratio == null) return "—";
  return `${Math.round(ratio * 100)}%`;
}

/** Money formatter — server returns Number, currency is ISO. */
export function fmtMoney(n, currency) {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || ""} ${Math.round(n).toLocaleString()}`.trim();
  }
}

// Shared table cell styles. Reports all use the same look.
export const reportTableStyles = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: T.fsSm,
    background: T.card,
  },
  thead: {
    borderBottom: "1px solid var(--v2-line)",
  },
  th: (align = "left") => ({
    padding:       "10px 14px",
    textAlign:     align,
    fontSize:      T.fsXxs,
    fontWeight:    T.fwBold,
    color:         "var(--v2-mid)",
    textTransform: "uppercase",
    letterSpacing: ".07em",
  }),
  tr: {
    borderBottom: `1px solid ${T.border}`,
    transition: "background 0.15s",
    cursor: "pointer",
  },
  td: (align = "left") => ({
    padding: "10px 12px",
    textAlign: align,
    fontSize: T.fsBase,
    color: T.dark,
  }),
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 6,
    padding: "2px 10px",
    fontSize: T.fsXs,
    fontWeight: T.fwSemibold,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
};

/**
 * Sortable table header cell shared across all three portfolio reports.
 *
 * @param {string}   label        - Visible column label.
 * @param {string}   sortKey      - The data key this column sorts by.
 * @param {string}   currentSort  - The currently active sort key.
 * @param {function} onSort       - Callback called with sortKey when clicked.
 * @param {string}   [align]      - Text alignment ("left" | "right"). Default "left".
 * @param {string}   ariaSort     - aria-sort value when this column is active ("ascending" | "descending").
 * @param {string}   testIdPrefix - Prefix for data-testid, e.g. "lease-countdown" → "lease-countdown-th-<sortKey>".
 */
export function SortableTh({ label, sortKey, currentSort, onSort, align = "left", ariaSort, testIdPrefix }) {
  const active = currentSort === sortKey;
  return (
    <th
      className="report-sort-th"
      data-testid={`${testIdPrefix}-th-${sortKey}`}
      data-sort-dir={active ? (ariaSort === "ascending" ? "asc" : "desc") : undefined}
      aria-sort={active ? ariaSort : "none"}
      tabIndex={0}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort(sortKey); } }}
      style={{ ...reportTableStyles.th(align), cursor: "pointer", color: active ? T.blue : T.mid }}
    >
      {label}<TableSortCaret active={active} dir={ariaSort === "ascending" ? 1 : -1} />
    </th>
  );
}

// ── Legend header helpers ─────────────────────────────────────────────────────
//
// LegendTh — a non-sortable <th> with a hover/focus-triggered ColorLegendPopup.
// Matches the HeadingTooltip pattern from LocationsListTable and LeasesListTable
// (UrgencyTh). Shared here so OpenRolesReport and OfficeCapacityReport don't
// duplicate the ref / timer / portal boilerplate.
//
// pillIndicator — colored pill swatch used as the legend row indicator when the
// table column itself renders a VerdictPill or capacity badge (not a dot).
// Exported so callers can build their legend constant arrays.

export function pillIndicator(bg, fg, text) {
  return (
    <span
      data-testid="pill-indicator"
      style={{
        display:      "inline-block",
        borderRadius: 4,
        padding:      "1px 5px",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwBold,
        background:   bg,
        color:        fg,
        whiteSpace:   "nowrap",
      }}
    >
      {text}
    </span>
  );
}

const _TH_TIP_STYLE = {
  background:    T.tooltip.bg,
  color:         T.tooltip.fg,
  borderRadius:  8,
  boxShadow:     T.tooltip.shadow,
  padding:       "13px 16px",
  zIndex:        20,
  textTransform: "none",
};

const _SR_ONLY = {
  position:   "absolute",
  width:      1,
  height:     1,
  padding:    0,
  margin:     -1,
  overflow:   "hidden",
  clip:       "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border:     0,
};

/**
 * Non-sortable table header cell with a hover/focus-triggered ColorLegendPopup.
 *
 * @param {string}    label       - Visible column label (rendered with dotted underline).
 * @param {ReactNode} popup       - The <ColorLegendPopup> to show in the portal.
 * @param {string}    tooltipId   - id used for aria-describedby (must be unique on page).
 * @param {string}    tooltipText - Plain-text SR fallback shown until popup opens.
 * @param {object}    [thStyle]   - Extra inline styles merged onto the <th>.
 */
export function LegendTh({ label, popup, tooltipId, tooltipText, thStyle }) {
  const thRef         = useRef(null);
  const { open: tipOpen, triggerProps, popupProps } = useHoverIntent({ interactive: true });

  return (
    <th
      ref={thRef}
      data-testid={`legend-th-${tooltipId}`}
      tabIndex={0}
      aria-describedby={tipOpen ? `${tooltipId}-popup` : tooltipId}
      aria-haspopup="true"
      aria-expanded={tipOpen}
      {...triggerProps}
      style={{
        ...reportTableStyles.th("left"),
        cursor: "help",
        ...thStyle,
      }}
    >
      <span style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>
        {label}
      </span>
      {!tipOpen && (
        <span id={tooltipId} data-testid={`th-desc-${tooltipId}`} style={_SR_ONLY}>
          {tooltipText}
        </span>
      )}
      {tipOpen && (
        <HeadingTooltip
          triggerRef={thRef}
          id={`${tooltipId}-popup`}
          role="tooltip"
          data-testid={`th-tooltip-${tooltipId}`}
          style={_TH_TIP_STYLE}
          {...popupProps}
        >
          {popup}
        </HeadingTooltip>
      )}
    </th>
  );
}

/**
 * Color-on-color tile used in the band summary strip at the top of each report.
 *
 * When onClick is provided the tile becomes a toggle button (primary filter).
 * The active tile shows a high-contrast outline so the selected state is clear.
 * Region pill counts must then respect this selection — see principles C.3.
 */
export function BandTile({ band, count, onClick, active }) {
  const isClickable = typeof onClick === "function";
  return (
    <div
      data-testid={`band-tile-${band.key}`}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={isClickable ? active : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      style={{
        padding: 12,
        background: band.bg,
        color: band.color,
        borderRadius: 8,
        textAlign: "center",
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        border: active ? `2px solid ${T.dark}` : `1px solid ${T.border}`,
        minWidth: 0,
        cursor: isClickable ? "pointer" : undefined,
        opacity: isClickable && !active && active !== undefined ? 0.7 : 1,
      }}
    >
      <div style={{ fontSize: T.fsLgX5, fontWeight: T.fwDisplay, marginBottom: 2, letterSpacing: -0.4 }}>
        {count}
      </div>
      {band.label}
    </div>
  );
}
